import React, { useState, useMemo } from 'react';
import { Button } from '@/components';
import { FilterPopup, FilterGroup } from '@/components';

interface UniversalFilterConfig {
  key: string;
  title: string;
  icon: string;
  multiple?: boolean;
  dataSource?: any[];
  staticOptions?: Array<{ key: string; label: string; value: string | boolean }>;
  labelField?: string;
  labelFormatter?: (item: any) => string;
  valueField?: string;
}

interface UniversalFilterProps {
  filters: UniversalFilterConfig[];
  currentFilters?: Record<string, any>;
  onApply: (filters: Record<string, any>) => void;
  onClear: () => void;
  className?: string;
  buttonText?: string;
  buttonIcon?: string;
}

const UniversalFilter: React.FC<UniversalFilterProps> = ({
  filters,
  currentFilters = {},
  onApply,
  onClear,
  className = '',
  buttonText = 'Filter',
  buttonIcon = 'ri-filter-3-fill',
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const filterGroups: FilterGroup[] = useMemo(() => {
    return filters.map((filter) => {
      let options: Array<{ key: string; label: string; value: string | boolean }> = [];

      if (filter.staticOptions) {
        options = filter.staticOptions;
      } else if (filter.dataSource && filter.dataSource.length > 0) {
        const valueField = filter.valueField || 'ID';
        options = filter.dataSource.map((item) => {
          let label: string;
          if (filter.labelFormatter) {
            label = filter.labelFormatter(item);
          } else {
            const labelField = filter.labelField || 'name';
            label = item[labelField] || item.name || 'Unknown';
          }
          return {
            key: `${filter.key}_${item[valueField] || item.id}`,
            label,
            value: String(item[valueField] || item.id),
          };
        });
      }

      return {
        title: filter.title,
        key: filter.key,
        multiple: filter.multiple ?? true,
        icon: filter.icon,
        options,
      };
    });
  }, [filters]);

  const handleApply = (appliedFilters: Record<string, any>) => {
    onApply(appliedFilters);
    setIsFilterOpen(false);
  };

  const handleClear = () => { onClear(); setIsFilterOpen(false); };
  const handleClose = () => setIsFilterOpen(false);

  const selectedFiltersCount = useMemo(() => {
    let count = 0;
    Object.keys(currentFilters).forEach((key) => {
      const filterValue = currentFilters[key];
      if (Array.isArray(filterValue)) count += filterValue.length;
      else if (filterValue !== undefined && filterValue !== null && filterValue !== '') count += 1;
    });
    return count;
  }, [currentFilters]);

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="neutral"
        className="flex border-stroke-sub-300! bg-bg-white-0! text-text-sub-600! hover:bg-bg-weak-50! border"
        size="small"
        onClick={() => setIsFilterOpen(true)}
      >
        <i
          className={`${buttonIcon} text-[12px] ${
            selectedFiltersCount > 0 ? 'text-primary' : ''
          } mr-1`}
        ></i>
        {buttonText}
        {selectedFiltersCount > 0 && (
          <span className="ml-1 bg-primary text-static-white text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[16px] h-4 flex items-center justify-center">
            {selectedFiltersCount}
          </span>
        )}
      </Button>

      {isFilterOpen && (
        <FilterPopup
          isOpen={isFilterOpen}
          onClose={handleClose}
          onApply={handleApply}
          onClear={handleClear}
          currentFilters={currentFilters}
          filterGroups={filterGroups}
        />
      )}
    </div>
  );
};

export default UniversalFilter;
